// Compact "Change image" button used inside the IdentityCard of every
// proptree-settings surface. Wraps a hidden <input type=file> + the
// uploadImage() helper + a busy / error inline state. The caller gets
// just the public CDN URL on success — what they do with it (e.g.
// `set('profileImage', url)`) is up to them.
import * as React from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';
import {
  uploadImage,
  type ImageUploadKind,
} from '../lib/uploadImage.js';

export function ImageUploadButton({
  kind,
  proptreeId,
  onUploaded,
  label = 'Change image',
  disabled,
  className,
}: {
  kind: ImageUploadKind;
  // Required for `proptree-*` kinds only.
  proptreeId?: string;
  onUploaded: (publicUrl: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const url = await uploadImage({ file, kind, proptreeId });
      onUploaded(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={cn('inline-flex flex-wrap items-center gap-3', className)}>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={busy || disabled}
      >
        {busy ? <Loader2 className="animate-spin" /> : <ImagePlus />}
        {busy ? 'Uploading…' : label}
      </Button>
      {error && (
        <span className="font-sans text-[12px] text-destructive">{error}</span>
      )}
    </div>
  );
}
